import Add16 from '@carbon/icons-react/es/add/16';
import { ColumnsType } from 'antd/es/table';
import { keyBy } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Role, Team } from 'api/models';
import { Button } from 'components/atoms/button';
import { PlaceholderAvatar } from 'components/molecules/placeholder-avatar';
import { useTranslation } from 'hooks/use-translation';
import { sortByText } from 'utils/sort-by-text';
import { RoleSelectionDropdown } from '../../role-selection';
import { AddToProjectRecordType } from '../../types';
import { getPlaceholderRoleOptions } from '../../utils/role-options';
import { ModalContent, NameCellWithoutPadding } from './common';

const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

interface Props {
  teams: Team[];
  roles: Role[];
  selectedTeams: Team[];
  updateSelectedTeams: (users: Team[]) => void;
  newPlaceholderClickHandler: () => void;
}

export const AddTeamsModalContent: FC<Props> = ({
  teams = [],
  roles,
  selectedTeams,
  updateSelectedTeams,
  newPlaceholderClickHandler,
}) => {
  const roleLookup = useMemo(() => keyBy(roles, 'roleId'), [roles]);
  const searchPlaceholder = useTranslation(
    'resource_management.add_placeholders.search.placeholder',
  );

  const [choosenRoles, setChoosenRoles] = useState<Role[]>([]);
  const choosenRoleIds = useMemo(
    () => choosenRoles.map(({ roleId }) => roleId),
    [choosenRoles],
  );

  const filterFunction = useCallback(
    (team: Team, searchText: string) =>
      (choosenRoleIds.length === 0 ||
        Boolean(team.roleId && choosenRoleIds.includes(team.roleId))) &&
      Boolean(
        searchText === '' ||
          team.teamName.toLowerCase().includes(searchText.toLowerCase()),
      ),
    [choosenRoleIds],
  );

  const teamSorter = useCallback(
    ({ entity: a }, { entity: b }) => sortByText(a.teamName, b.teamName),
    [],
  );

  const renderTeam = useCallback(
    (team: Team) => (
      <NameCellWithoutPadding
        primaryText={team.teamName}
        secondaryText={team.roleId && roleLookup[team.roleId]?.roleName}
        logo={<PlaceholderAvatar isCustomerRole={false} name={team.teamName} />}
      />
    ),
    [roleLookup],
  );

  const actionBar = (
    <ActionBar>
      <RoleSelectionDropdown
        roleOptions={getPlaceholderRoleOptions(roles, teams)}
        numberOfEntities={teams?.length ?? 0}
        choosenRoles={choosenRoles}
        onRoleSelectionChange={setChoosenRoles}
      />
      <Button
        type="link"
        icon={<Add16 />}
        iconPlacement="right"
        onClick={() => {
          newPlaceholderClickHandler();
        }}
      >
        <FormattedMessage id="resource_management.add_placeholders.new_placeholder" />
      </Button>
    </ActionBar>
  );

  const columns: ColumnsType<AddToProjectRecordType<Team>> = [
    {
      title: useTranslation('placeholders'),
      dataIndex: 'entity',
      key: 'entity',
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: 'ascend',
      sorter: teamSorter,
      render: renderTeam,
    },
  ];

  return (
    <ModalContent
      entityIdKey="teamId"
      actionBar={actionBar}
      entities={teams}
      searchPlaceholder={searchPlaceholder}
      filterFunction={filterFunction}
      selectedEntities={selectedTeams}
      updateSelectedEntities={updateSelectedTeams}
      columns={columns}
      isMultiSelect
    />
  );
};
